import React, { useState, useEffect, useRef } from 'react';
import { useForm, useWatch, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { MinistriesService, type CreateEventoDto, type Evento } from '../../services/ministries/ministries.service';
import { NotificationToast } from '../ui/NotificationToast';

const createEventoSchema = z.object({
  titulo: z.string().min(2, 'Mínimo 2 caracteres'),
  descripcion: z.string().optional(),
  fecha: z.string().min(1, 'La fecha es requerida'),
  horaInicio: z.string().optional(),
  horaFin: z.string().optional(),
  lugar: z.string().optional(),
  estado: z.enum(['programado', 'en_curso', 'finalizado', 'cancelado', 'aplazado']).default('programado'),
  motivoEstado: z.string().optional(),
  costoPorPersona: z.preprocess((val) => (val === '' || val === null || val === undefined ? undefined : Number(val)), z.number().min(0).optional()),
  presupuesto: z.preprocess((val) => (val === '' || val === null || val === undefined ? undefined : Number(val)), z.number().min(0).optional()),
  notasPresupuesto: z.string().optional(),
  requiereInscripcion: z.boolean().default(false),
  capacidadMaxima: z.preprocess((val) => (val === '' || val === null || val === undefined ? undefined : Number(val)), z.number().min(1).optional()),
  esPublico: z.boolean().default(false),
}).refine((data) => {
  if (data.estado === 'cancelado' || data.estado === 'aplazado') {
    return !!data.motivoEstado && data.motivoEstado.trim().length > 0;
  }
  return true;
}, {
  message: 'El motivo es requerido cuando el estado es cancelado o aplazado',
  path: ['motivoEstado'],
});

type CreateEventoFormData = z.infer<typeof createEventoSchema>;

interface CreateEventoDrawerProps {
  isOpen: boolean;
  ministerioId: string;
  onClose: () => void;
  onSuccess?: () => void;
  defaultDate?: string;
  evento?: Evento | null;
}

export const CreateEventoDrawer: React.FC<CreateEventoDrawerProps> = ({
  isOpen,
  ministerioId,
  onClose,
  onSuccess,
  defaultDate,
  evento,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [showNotification, setShowNotification] = useState(false);
  const [notificationConfig, setNotificationConfig] = useState<{
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  } | null>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    setValue,
  } = useForm<CreateEventoFormData>({
    resolver: zodResolver(createEventoSchema),
    defaultValues: {
      titulo: '',
      descripcion: '',
      fecha: '',
      horaInicio: '',
      horaFin: '',
      lugar: '',
      estado: 'programado',
      motivoEstado: '',
      costoPorPersona: undefined,
      presupuesto: undefined,
      notasPresupuesto: '',
      requiereInscripcion: false,
      capacidadMaxima: undefined,
      esPublico: false,
    },
  });

  const estado = useWatch({ control, name: 'estado' });
  const requiereInscripcion = useWatch({ control, name: 'requiereInscripcion' });
  const esPublicoValue = useWatch({ control, name: 'esPublico' });

  const isEditing = !!evento;

  // Efecto para limpiar timeout de cierre cuando se vuelve a abrir
  useEffect(() => {
    if (!isOpen) {
      setIsVisible(false);
      closeTimeoutRef.current = setTimeout(() => {
        setIsLoading(false);
        setErrorMessage(null);
        setShowNotification(false);
        setNotificationConfig(null);
        reset();
      }, 300);
    }
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
    };
  }, [isOpen, reset]);

  // Efecto para precargar datos del evento al abrir
  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(() => setIsVisible(true), 10);
    if (evento) {
      reset({
        titulo: evento.titulo,
        descripcion: evento.descripcion || '',
        fecha: evento.fecha.slice(0, 10),
        horaInicio: evento.horaInicio || '',
        horaFin: evento.horaFin || '',
        lugar: evento.lugar || '',
        estado: evento.estado as any,
        motivoEstado: evento.motivoEstado || '',
        costoPorPersona: evento.costoPorPersona,
        presupuesto: evento.presupuesto,
        notasPresupuesto: evento.notasPresupuesto || '',
        requiereInscripcion: evento.requiereInscripcion,
        capacidadMaxima: evento.capacidadMaxima,
        esPublico: evento.esPublico ?? false,
      });
    } else if (defaultDate) {
      reset({
        titulo: '',
        descripcion: '',
        fecha: defaultDate,
        horaInicio: '',
        horaFin: '',
        lugar: '',
        estado: 'programado',
        motivoEstado: '',
        costoPorPersona: undefined,
        presupuesto: undefined,
        notasPresupuesto: '',
        requiereInscripcion: false,
        capacidadMaxima: undefined,
        esPublico: false,
      });
    }
    return () => clearTimeout(timer);
  }, [isOpen, evento, defaultDate, reset]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const onSubmit = async (data: CreateEventoFormData) => {
    setIsLoading(true);
    setErrorMessage(null);
    try {
      const basePayload = {
        titulo: data.titulo,
        descripcion: data.descripcion || undefined,
        fecha: data.fecha,
        horaInicio: data.horaInicio || undefined,
        horaFin: data.horaFin || undefined,
        lugar: data.lugar || undefined,
        ministerioIds: [ministerioId],
        estado: data.estado,
        motivoEstado: data.motivoEstado || undefined,
        costoPorPersona: data.costoPorPersona,
        presupuesto: data.presupuesto,
        notasPresupuesto: data.notasPresupuesto || undefined,
        requiereInscripcion: data.requiereInscripcion,
        capacidadMaxima: data.capacidadMaxima,
        esPublico: data.esPublico,
      };

      if (isEditing && evento) {
        const result = await MinistriesService.updateEvento(evento.id, basePayload);
        setNotificationConfig({
          title: '¡Evento actualizado!',
          message: `${result.titulo} fue actualizado exitosamente.`,
          type: 'success',
        });
      } else {
        const userDataRaw = localStorage.getItem('userData');
        const userData = userDataRaw ? (JSON.parse(userDataRaw) as { id?: string }) : null;
        const payload: CreateEventoDto = {
          ...basePayload,
          creadorId: userData?.id || '',
        };
        const result = await MinistriesService.createEvento(ministerioId, payload);
        setNotificationConfig({
          title: '¡Evento creado!',
          message: `${result.titulo} fue creado exitosamente.`,
          type: 'success',
        });
      }
      setShowNotification(true);
      if (onSuccess) onSuccess();
      // Dar tiempo a que se vea el toast antes de cerrar el drawer
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (error) {
      console.error('Error al guardar evento:', error);
      const errorMsg = error instanceof Error ? error.message : 'Error desconocido al guardar evento';
      setErrorMessage(errorMsg);
      setNotificationConfig({
        title: isEditing ? 'Error al actualizar evento' : 'Error al crear evento',
        message: errorMsg,
        type: 'error',
      });
      setShowNotification(true);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const mostrarMotivo = estado === 'cancelado' || estado === 'aplazado';

  return (
    <>
      {notificationConfig && (
        <NotificationToast
          isOpen={showNotification}
          title={notificationConfig.title}
          message={notificationConfig.message}
          type={notificationConfig.type}
          onClose={() => {
            setShowNotification(false);
            setNotificationConfig(null);
          }}
        />
      )}
      <div className="fixed left-0 right-0 bottom-0 top-0 z-[70] overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div
            className="absolute inset-0 cursor-pointer"
            onClick={handleClose}
            aria-label="Cerrar drawer"
          />
          <section
            className={`absolute right-0 top-0 h-full w-full max-w-2xl border-l border-gray-200 transform transition-transform duration-300 ease-in-out ${
              isVisible ? 'translate-x-0' : 'translate-x-full'
            }`}
          >
            <div
              className="h-full flex flex-col bg-white shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 bg-gray-100 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xs font-medium text-gray-900">{isEditing ? 'Editar Evento' : 'Crear Evento'}</h2>
                  <button
                    onClick={handleClose}
                    className="text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                {errorMessage && (
                  <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex">
                      <svg className="w-5 h-5 text-red-400 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-red-800 text-xs">{errorMessage}</p>
                    </div>
                  </div>
                )}

                <form id="create-evento-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  {/* Título */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">Título *</label>
                    <input
                      type="text"
                      {...register('titulo')}
                      placeholder="Título del evento"
                      className={`w-full px-4 py-3 border rounded-lg transition-all ${
                        errors.titulo
                          ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                          : 'border-gray-300 focus:border-purple-500 focus:ring-purple-500/20'
                      } focus:ring-4 focus:outline-none`}
                    />
                    {errors.titulo && <p className="mt-1 text-xs text-red-500">{errors.titulo.message}</p>}
                  </div>

                  {/* Descripción */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">Descripción</label>
                    <textarea
                      {...register('descripcion')}
                      rows={3}
                      placeholder="Descripción del evento"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg transition-all focus:border-purple-500 focus:ring-purple-500/20 focus:ring-4 focus:outline-none"
                    />
                  </div>

                  {/* Fecha, horas */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">Fecha *</label>
                      <input
                        type="date"
                        {...register('fecha')}
                        className={`w-full px-4 py-3 border rounded-lg transition-all ${
                          errors.fecha
                            ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                            : 'border-gray-300 focus:border-purple-500 focus:ring-purple-500/20'
                        } focus:ring-4 focus:outline-none`}
                      />
                      {errors.fecha && <p className="mt-1 text-xs text-red-500">{errors.fecha.message}</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">Hora inicio</label>
                      <input
                        type="time"
                        {...register('horaInicio')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg transition-all focus:border-purple-500 focus:ring-purple-500/20 focus:ring-4 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">Hora fin</label>
                      <input
                        type="time"
                        {...register('horaFin')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg transition-all focus:border-purple-500 focus:ring-purple-500/20 focus:ring-4 focus:outline-none"
                      />
                    </div>
                  </div>

                  {/* Lugar */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">Lugar</label>
                    <input
                      type="text"
                      {...register('lugar')}
                      placeholder="Lugar del evento"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg transition-all focus:border-purple-500 focus:ring-purple-500/20 focus:ring-4 focus:outline-none"
                    />
                  </div>

                  {/* Estado y Motivo */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">Estado</label>
                      <select
                        {...register('estado')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg transition-all focus:border-purple-500 focus:ring-purple-500/20 focus:ring-4 focus:outline-none bg-white"
                      >
                        <option value="programado">Programado</option>
                        <option value="en_curso">En curso</option>
                        <option value="finalizado">Finalizado</option>
                        <option value="cancelado">Cancelado</option>
                        <option value="aplazado">Aplazado</option>
                      </select>
                    </div>
                    {mostrarMotivo && (
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-2">Motivo *</label>
                        <input
                          type="text"
                          {...register('motivoEstado')}
                          placeholder="Motivo de cancelación o aplazamiento"
                          className={`w-full px-4 py-3 border rounded-lg transition-all ${
                            errors.motivoEstado
                              ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                              : 'border-gray-300 focus:border-purple-500 focus:ring-purple-500/20'
                          } focus:ring-4 focus:outline-none`}
                        />
                        {errors.motivoEstado && <p className="mt-1 text-xs text-red-500">{errors.motivoEstado.message}</p>}
                      </div>
                    )}
                  </div>

                  {/* Costos */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">Costo por persona ($)</label>
                      <input
                        type="number"
                        min={0}
                        step="100"
                        {...register('costoPorPersona')}
                        placeholder="Ej: 45000"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg transition-all focus:border-purple-500 focus:ring-purple-500/20 focus:ring-4 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">Presupuesto estimado ($)</label>
                      <input
                        type="number"
                        min={0}
                        step="1000"
                        {...register('presupuesto')}
                        placeholder="Ej: 500000"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg transition-all focus:border-purple-500 focus:ring-purple-500/20 focus:ring-4 focus:outline-none"
                      />
                    </div>
                  </div>

                  {/* Notas de presupuesto */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">Notas de presupuesto</label>
                    <textarea
                      {...register('notasPresupuesto')}
                      rows={2}
                      placeholder="Detalles del presupuesto o gastos previstos"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg transition-all focus:border-purple-500 focus:ring-purple-500/20 focus:ring-4 focus:outline-none"
                    />
                  </div>

                  {/* Visibilidad pública */}
                  <div className="flex items-center gap-3 bg-purple-50 rounded-lg p-4 border border-purple-100">
                    <Controller
                      name="esPublico"
                      control={control}
                      render={({ field }) => (
                        <input
                          type="checkbox"
                          id="esPublico"
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                        />
                      )}
                    />
                    <div className="flex-1">
                      <label htmlFor="esPublico" className="text-xs font-medium text-gray-700 cursor-pointer">
                        Hacer público este evento
                      </label>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Si está activo, el evento será visible en la página pública de eventos y en el calendario. De lo contrario, solo los usuarios autenticados con acceso al ministerio podrán verlo.
                      </p>
                    </div>
                  </div>

                  {/* Requiere inscripción */}
                  <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-4">
                    <input
                      type="checkbox"
                      id="requiereInscripcion"
                      {...register('requiereInscripcion')}
                      className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    />
                    <label htmlFor="requiereInscripcion" className="text-xs font-medium text-gray-700">
                      Este evento requiere inscripción previa
                    </label>
                  </div>

                  {/* Capacidad máxima */}
                  {requiereInscripcion && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">Capacidad máxima</label>
                      <input
                        type="number"
                        min={1}
                        {...register('capacidadMaxima')}
                        placeholder="Número máximo de participantes"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg transition-all focus:border-purple-500 focus:ring-purple-500/20 focus:ring-4 focus:outline-none"
                      />
                    </div>
                  )}
                </form>
              </div>

              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    disabled={isLoading}
                    onClick={() => handleSubmit(onSubmit)()}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {isLoading && (
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    )}
                    {isEditing ? 'Guardar cambios' : 'Guardar'}
                  </button>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};
